/**
 * BrowserCompatibilityAlert Component
 *
 * Displays a warning when the File System Access API is not supported
 * AND the user is using (or has no provider selected, defaulting to) local provider.
 * Cloud sync (Google Drive, Dropbox, etc.) doesn't need File System Access API.
 */

// 1. External dependencies
import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';

// 2. Internal absolute imports
import { Alert } from '@enfomentis/ui';
import type { SyncProviderType } from '@/types/sync';

export interface BrowserCompatibilityAlertProps {
  /** Currently active sync provider */
  activeSyncProvider: SyncProviderType | undefined;
}

/**
 * Browser Compatibility Alert
 *
 * Detects File System Access API support and displays a warning only when:
 * 1. API is not supported
 * 2. User is using local provider (or no provider set, which defaults to local)
 *
 * Cloud sync providers (Google Drive, etc.) don't require File System Access API.
 */
export const BrowserCompatibilityAlert: React.FC<BrowserCompatibilityAlertProps> = ({
  activeSyncProvider
}) => {
  const [hasFileSystemAPI, setHasFileSystemAPI] = useState<boolean>(true);

  useEffect(() => {
    // Feature detection for File System Access API
    const isSupported = 'showDirectoryPicker' in window;
    setHasFileSystemAPI(isSupported);
  }, []);

  // Only show alert if:
  // 1. File System API is not supported
  // 2. User is using local provider OR no provider is set (defaults to local)
  const shouldShowAlert = !hasFileSystemAPI && (!activeSyncProvider || activeSyncProvider === 'local');

  if (!shouldShowAlert) {
    return null;
  }

  return (
    <Alert color="warning" variant="bordered" className="mb-6">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-warning mb-1 break-words">
            Limited Browser Support
          </h4>
          <p className="text-sm text-text-secondary mb-3 break-words">
            Your browser doesn't support the File System Access API needed for automatic sync.
            For the best experience, please use Chrome or Edge. Alternatively, you can manually
            download and upload sync files (coming in Task 10).
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <a
              href="https://www.google.com/chrome"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline break-words"
            >
              Download Chrome →
            </a>
            <span className="text-text-tertiary flex-shrink-0">or</span>
            <a
              href="https://www.microsoft.com/edge"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline break-words"
            >
              Download Edge →
            </a>
          </div>
        </div>
      </div>
    </Alert>
  );
};
